import { useCallback, useEffect, useRef, useState } from "react";

import { doNegotiation } from "../../../../hooks/reactQueryHooks/useMentors";

export interface IUseWebRTCProps {
  mentorId: string;
  userMicrophoneStream: MediaStream | null;
  selectedMicrophone: MediaDeviceInfo | null;
  apiKey: string;
  onMessage?: (ev: {
    event: string;
    data?: {
      state?: string;
      message?: { action?: string; content?: string; role?: string };
    };
  }) => any;
}

const config: RTCConfiguration = {
  iceServers: [
    { urls: "stun:stun1.ciscape.com:3478" },
    {
      urls: "turn:stun1.ciscape.com:3478",
      username: "tcuser",
      credential: "dw4k_BR2f-Y4A-@aT7ip@Qzs",
    },
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export const useWebRTC = ({
  mentorId,
  userMicrophoneStream,
  selectedMicrophone,
  apiKey,
  onMessage,
}: IUseWebRTCProps) => {
  const peerConnectionRef = useRef<RTCPeerConnection>();
  const [videoStream, setVideoStream] = useState<MediaStream>();
  const [audioStream, setAudioStream] = useState<MediaStream>();
  const audioSenderRef = useRef<RTCRtpTransceiver | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);

  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef<boolean>(true);

  const handleConnectionStateChange = () => {
    if (peerConnectionRef.current) {
      console.log(
        "Connection state:",
        peerConnectionRef.current.connectionState,
      );
    }
  };

  const handleMessage = useCallback(
    (evt: MessageEvent) => {
      const data = JSON.parse(evt.data);
      onMessage?.(data);
    },
    [onMessage],
  );

  const handlePeerConnectionTrack = (event: RTCTrackEvent) => {
    const remoteStream = event.streams[0];

    // If the remote peer sends video, set it to the video element
    if (event.track.kind === "video") {
      setVideoStream(remoteStream);
    }

    // If the remote peer sends audio, set it to the audio element
    if (event.track.kind === "audio") {
      setAudioStream(remoteStream);
    }
  };

  const negotiate = async (_peerConnection: RTCPeerConnection) => {
    const audioSender = _peerConnection.addTransceiver("audio", {
      direction: "sendrecv",
    });
    audioSenderRef.current = audioSender;
    if (
      userMicrophoneStream &&
      userMicrophoneStream.getAudioTracks().length > 0
    ) {
      const audioTrack = userMicrophoneStream.getAudioTracks()[0];
      void audioSender.sender.replaceTrack(audioTrack);
    }

    _peerConnection.addTransceiver("video", { direction: "recvonly" });

    console.log("Creating offer");
    _peerConnection.onnegotiationneeded = async () => {
      const offer = await _peerConnection.createOffer();
      await _peerConnection.setLocalDescription(offer);
    };

    console.log("Waiting for ICE gathering...");
    await new Promise<void>(resolve => {
      if (_peerConnection.iceGatheringState === "complete") {
        resolve();
      } else {
        const checkState = () => {
          if (_peerConnection.iceGatheringState === "complete") {
            _peerConnection.removeEventListener(
              "icegatheringstatechange",
              checkState,
            );
            resolve();
          }
        };
        _peerConnection.addEventListener("icegatheringstatechange", checkState);
      }
    });

    const offerLocal = _peerConnection.localDescription;

    _peerConnection.ontrack = event => {
      console.log("here track recived");
      const remoteStream = new MediaStream();
      event.streams[0].getTracks().forEach(track => {
        remoteStream.addTrack(track);
      });

      const audioElement = new Audio();
      audioElement.srcObject = remoteStream;
      void audioElement.play();
    };

    const answerContent = await doNegotiation({
      mentorId: mentorId,
      offer: offerLocal as RTCSessionDescription,
      apiKey,
    });

    if (_peerConnection.signalingState === "have-local-offer") {
      await _peerConnection.setRemoteDescription(
        answerContent.answer as RTCSessionDescriptionInit,
      );
    } else {
      throw new Error(
        `Invalid signaling state: ${_peerConnection.signalingState}`,
      );
    }

    setSessionId(answerContent.session_id);
  };

  // Set up the WebRTC peer connection and media stream
  const startConnection = async () => {
    if (!isMounted.current) return;
    isMounted.current = true;
    setIsLoading(true);
    try {
      // Create a new peer connection
      const peerConnection = new RTCPeerConnection(config);
      peerConnectionRef.current = peerConnection;

      // Handle connection state changes
      peerConnection.addEventListener(
        "connectionstatechange",
        handleConnectionStateChange,
      );

      // Handle track events (audio/video) from the remote peer
      peerConnection.addEventListener("track", handlePeerConnectionTrack);

      // Create a new data channel
      const dataChannel = peerConnection.createDataChannel("chat", {
        ordered: true,
      });
      dataChannelRef.current = dataChannel;
      dataChannel.onmessage = handleMessage;

      // Handle negotiation
      await negotiate(peerConnection);
    } catch (error) {
      console.error("Error in WebRTC connection:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup function
  const closeConnection = () => {
    console.log("Closing WebRTC connection...");
    isMounted.current = false;

    // Close DataChannel
    if (dataChannelRef.current) {
      dataChannelRef.current.onmessage = null;
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach(sender => {
        if (sender.track) {
          sender.track.stop();
        }
      });
      peerConnectionRef.current.removeEventListener(
        "connectionstatechange",
        handleConnectionStateChange,
      );
      peerConnectionRef.current.removeEventListener(
        "track",
        handlePeerConnectionTrack,
      );
      peerConnectionRef.current.close();
      peerConnectionRef.current = undefined;
    }
  };

  // Start WebRTC connection when component mounts
  useEffect(() => {
    if (!mentorId || !apiKey) return;

    console.log("Starting WebRTC connection...");
    void startConnection();

    return closeConnection; // Cleanup when component unmounts
  }, [mentorId, apiKey]);

  useEffect(() => {
    if (
      peerConnectionRef.current &&
      audioSenderRef.current &&
      userMicrophoneStream
    ) {
      const newAudioTrack = userMicrophoneStream.getAudioTracks()[0];
      if (newAudioTrack) {
        audioSenderRef.current.sender
          .replaceTrack(newAudioTrack)
          .catch(error =>
            console.error("Failed to replace audio track:", error),
          );
      }
    }
  }, [userMicrophoneStream, selectedMicrophone]);

  return {
    sessionId,
    videoStream,
    audioStream,
    dataChannel: dataChannelRef?.current,
    closeConnection,
    isLoading,
    error,
  };
};
